import { AfterViewInit, Component, ContentChild, HostBinding, Input, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { produce } from 'immer';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Confirmation, ConfirmationService, MenuItem } from 'primeng/api';
import { Button } from 'primeng/button';
import { ContextMenu } from 'primeng/contextmenu';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LazyLoadedWidgets } from 'src/app/app-routing-lazy';
import { SmartActionContext } from 'src/app/models/smart-actions.models';
import { LazyLoaderDirective } from 'src/app/shared/lazy-loader/lazy-loader.module';
import { LazyLoaderService } from 'src/app/shared/lazy-loader/lazy-loader.service';
import { ILazyComponentHandler, ISmartActionConfigOptions, ISmartActionMetadata, SmartActionContextItem } from '../../models/smart-actions.models';
import { BaseComponent } from './../../shared/base.component';
import { SmartActionDisplayType } from './smart-actions.models';

@AutoUnsubscribe()
@Component({
    selector: 'app-smart-actions',
    templateUrl: './smart-actions.component.html',
    styleUrls: ['./smart-actions.component.scss']
})
export class SmartActionsComponent extends BaseComponent implements OnInit, ILazyComponentHandler {

    @ViewChild('contextMenu', { static: false }) contextMenu: ContextMenu;

    @ContentChild('wrapperTemplate', { static: true }) wrapperTemplate: TemplateRef<any>;

    @ViewChild('customSmartActionBtn', { static: false }) customSmartActionBtn: TemplateRef<any>;

    smartActionsBtn: Button;

    @Input() executeActions = false;
    @Input() buttonClass = 'p-button-success';

    private lazyComponentHandlerInternal: ILazyComponentHandler;
    currentCustomViewMetadata: any;

    customViewSync: string;

    get lazyComponentHandler() {
        return this.lazyComponentHandlerInternal;
    }

    @Input() zIndex: number;

    @HostBinding('style')
    get myStyle(): SafeStyle {
        if (this.zIndex != null) {
            return this.sanitizer.bypassSecurityTrustStyle(`z-index: ${this.zIndex};`);
        }

        return undefined;
    }

    private keyInternal: string;
    @Input()
    set key(value: string) {
        this.keyInternal = value;
        this.setMenuItems();
    }

    get key(): string {
        return this.keyInternal;
    }

    private optionsInternal: any;
    @Input()
    set options(value: any) {
        this.optionsInternal = value;
        this.setMenuItems();
    }

    get options() {
        return this.optionsInternal;
    }

    private _context: SmartActionContext;


    @Input()
    set context(value: SmartActionContext) {
        setTimeout(() => {
            this._context = value;
            this.lazyComponentHandlerInternal = this;
            this.setMenuItems();
        });
    }

    get context(): SmartActionContext {
        return this._context;
    }

    private _disabled: boolean;
    @Input() set disabled(value: boolean) {
        this._disabled = value;
    }

    get disabled() {
        let result = this._disabled;
        if (result === undefined) {
            result = this.getDefaultDisabled();
        }

        return result;
    }

    get label() {
        if (this.disabled) {
            return this.disabledByOnlyAction() ? this.context.activeActions[0].displayName : 'Not available';
        } else if (!this.context?.activeActions || this.context.activeActions.length === 0) {
            return 'No Actions';
        } else if (this.context.activeActions.length === 1) {
            return this.context.activeActions[0].displayName;
        } else if (this.context.activeActions.length > 1) {
            return `${this.context.activeActions.length}  Actions`;
        }

        return '';
    }

    get icon() {
        if (this.disabled) {
            return this.disabledByOnlyAction() ? this.context.activeActions[0].icon : 'fal fa-ban';
        } else if (!this.context?.activeActions || this.context.activeActions.length === 0) {
            return 'fal fa-ban';
        } else if (this.context.activeActions.length === 1) {
            return this.context.activeActions[0].icon;
        }

        return undefined;
    }

    menuItems: MenuItem[];

    lazyLoaderDirectives$$: Subscription;
    private readonly lazyLoaderDirectivesBehavior$ = new BehaviorSubject<LazyLoaderDirective[]>([]);
    lazyLoaderDirectives$ = this.lazyLoaderDirectivesBehavior$.asObservable();
    @ViewChildren(LazyLoaderDirective)
    set lazyLoaderDirectives(value: QueryList<LazyLoaderDirective>) {
        if (value) {
            this.lazyLoaderDirectivesBehavior$.next(value.toArray());
        } else {
            this.lazyLoaderDirectivesBehavior$.next([]);
        }
    }

    @Input() displayMode: SmartActionDisplayType = 'button';

    constructor(
        private readonly lazyLoaderService: LazyLoaderService,
        private readonly confirmationService: ConfirmationService,
        private readonly sanitizer: DomSanitizer
    ) {
        super();
    }

    ngOnInit() {
        this.configureLazyLoadingListener();
    }

    getDefaultDisabled() {
        return !this.context?.activeActions || this.context.activeActions.length === 0 || this.disabledByOnlyAction();
    }

    disabledByOnlyAction(): boolean {
        if (!this.context?.activeActions || this.context.activeActions.length !== 1) {
            return undefined;
        }

        return this.context.activeActions[0].disabled;
    }

    setMenuItems() {
        this.menuItems = [];
        let options = this.options || {};
        options = { ...options, targetUI: this.key };
        if (this.context?.actions) {
            this.menuItems = this.context?.actions.map(action => {
                return this.mapSmartActionToMenuItemFunc(this.context, action, options);
            }).filter(action => !!action);
        }

        // Change duplicate ids
        const idMap: string[] = [];
        this.menuItems.forEach((item, idx) => {
            if (item.id) {
                if (idMap.includes(item.id)) {
                    item.id = `${item.id}_${idx}`;
                } else {
                    idMap.push(item.id);
                }
            }
        });
    }

    configureLazyLoadingListener() {
        this.lazyLoaderDirectives$$ = this.lazyLoaderDirectives$.subscribe((directives: LazyLoaderDirective[]) => {
            this.lazyLoaderService.processDirectives(directives, this.key);
        });
    }

    takeAction(event: any, menu: ContextMenu) {
        if (this.menuItems && this.menuItems.length === 1) {
            this.menuItems[0].command({ item: this.menuItems[0] });
        } else {
            if (event.stopPropagation) {
                event.stopPropagation();
            }
            menu.toggle(event);
            return false;
        }
    }

    mapSmartActionToMenuItemFunc(context: SmartActionContext, action: SmartActionContextItem, options?: ISmartActionConfigOptions) {
        let result: MenuItem;
        if (!action) {
            return;
        }
        if (action.separator) {
            result = { separator: true } as MenuItem;

        } else {
            let isProcessingMenuItemCommand = false;
            result = ({
                id: action.id,
                label: action.displayName,
                escape: false,
                icon: action.icon,
                disabled: action.disabled || isProcessingMenuItemCommand,
                command: (commandContext: any) => {
                    if (isProcessingMenuItemCommand) return;
                    isProcessingMenuItemCommand = true;
                    commandContext.item.disabled = true;

                    const commandOptions = commandContext.item.options;

                    if (action.confirmation) {
                        this.showConfirmationDialog(this.confirmationService, commandContext.item, commandOptions?.targetUI);
                    } else {
                        const metadata = {
                            context: commandContext,
                            lazyComponentHandler: this.lazyComponentHandler,
                            sourceAction: action,
                            id: action.id,
                            options: commandContext.item.options,
                        } as ISmartActionMetadata;

                        action.command(metadata);
                    }

                    setTimeout(() => {
                        isProcessingMenuItemCommand = false;
                        commandContext.item.disabled = false;
                    }, 1000); // 10 second lock
                },
                options,
                lazyComponentHandler: this.lazyComponentHandler,
                sourceAction: action,
                automationId: { action },
            } as MenuItem);
        }

        if (action.children) {
            result.items = action.children.map(childAction => this.mapSmartActionToMenuItemFunc(context, childAction)).filter(actionItem => !!actionItem);
        }

        return result;
    }

    standaloneActionClick($event: Event, actionItem: SmartActionContextItem) {
        const mockedMenuItemClickEvent = {
            originalEvent: $event,
            item: actionItem
        };

        actionItem.command(mockedMenuItemClickEvent);
    }

    showConfirmationDialog(confirmationService: ConfirmationService, metadata: any, targetUI: string): boolean {
        const sourceAction = metadata?.sourceAction;
        const lazyComponentHandler = metadata && metadata.lazyComponentHandler as ILazyComponentHandler;
        const options = metadata?.options;
        if (
            options &&
            ((!options.targetUI && !targetUI) || options.targetUI === targetUI)
        ) {
            if (sourceAction.confirmation) {
                if (['dialog', 'popup'].some(typeItem => typeItem === sourceAction.confirmation.type)) {
                    const confirmation: Confirmation = {

                        header: sourceAction.confirmation.title,
                        message: sourceAction.confirmation.detailsExternal ? sourceAction.confirmation.details : `Do you want to continue to: ${metadata.label}`,
                        accept: async () => {
                            metadata = produce(metadata, (draft: { sourceAction: SmartActionContextItem }) => {
                                draft.sourceAction = { ...draft.sourceAction };
                                draft.sourceAction.confirmation = null;
                            });
                            try {
                                lazyComponentHandler.setCurrentCustomView(null);
                                await sourceAction.command(metadata);
                            } finally {
                            }
                        },
                        reject: () => {
                            lazyComponentHandler.setCurrentCustomView(null);
                        }
                    };

                    if (sourceAction.confirmation.type === 'dialog') {
                        confirmation.key = 'confirmDialog' + (targetUI || '');
                    } else if (sourceAction.confirmation.type === 'popup') {
                        confirmation.key = 'confirmPopup' + (targetUI || '');
                    }

                    setTimeout(() => {
                        confirmationService.confirm(confirmation);
                    });

                    return true;

                } else if ([null, undefined, 'custom'].some(typeItem => typeItem === sourceAction.confirmation.type)) {
                    sourceAction.confirmation.details = sourceAction.confirmation.detailsExternal ? sourceAction.confirmation.details : `Do you want to continue to: ${metadata.label}`;
                    lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_CONFIRMATION, metadata);
                }
            }
        }

        return false;

    }

    setCurrentCustomView(viewId: string, metadata?: any) {
        this.currentCustomViewMetadata = metadata;

        this.customViewSync = null;
        setTimeout(() => {
            this.customViewSync = viewId;
        });
    }
}
